/**
 * Newsletter Step 3 Component
 * Success celebration screen
 */

import { useEffect, useState } from 'react';
import { Mail, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { dataLayerPushClickButton } from '@/lib/ga4';
import { useNewsletter } from '../hooks/useNewsletter';
import { MODAL_CONTENT } from '../constants';

export function NewsletterStep3() {
  const { closeModal, formData } = useNewsletter();
  const [showContent, setShowContent] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);

  const content = MODAL_CONTENT.steps.step3;

  // Staggered animation
  useEffect(() => {
    const checkmarkTimer = setTimeout(() => setShowCheckmark(true), 100);
    const contentTimer = setTimeout(() => setShowContent(true), 400);
    return () => {
      clearTimeout(checkmarkTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-8 tablet:px-6 tablet:py-10 text-center">
        {/* Animated checkmark */}
        <div className="relative inline-flex items-center justify-center mb-6">
          {/* Background circle with pulse */}
          <div
            className={cn(
              'absolute inset-0 rounded-full bg-emerald-100',
              'transition-all duration-500',
              showCheckmark ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
            )}
          />
          {/* Animated pulse rings */}
          <div
            className={cn(
              'absolute inset-0 rounded-full bg-emerald-200',
              'animate-ping opacity-20',
              showCheckmark ? 'block' : 'hidden'
            )}
          />
          {/* Main checkmark container */}
          <div
            className={cn(
              'relative w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600',
              'flex items-center justify-center shadow-lg shadow-emerald-500/30',
              'transition-all duration-500',
              showCheckmark ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
            )}
          >
            {/* Animated checkmark SVG */}
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
                className={cn(
                  'transition-all duration-500 delay-200',
                  showCheckmark
                    ? 'stroke-dashoffset-0'
                    : ''
                )}
                style={{
                  strokeDasharray: 24,
                  strokeDashoffset: showCheckmark ? 0 : 24,
                  transition: 'stroke-dashoffset 0.5s ease-out 0.3s',
                }}
              />
            </svg>
          </div>
          {/* Sparkles */}
          <Sparkles
            className={cn(
              'absolute -top-1 -right-1 h-6 w-6 text-yellow-400',
              'transition-all duration-500 delay-300',
              showCheckmark ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
            )}
          />
        </div>

        {/* Text content */}
        <div
          className={cn(
            'transition-all duration-500',
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          )}
        >
          <h3 className="text-xl tablet:text-2xl font-bold text-gray-900 mb-2">
            {content.title}
          </h3>
          <p className="text-sm tablet:text-base text-gray-600 mb-4">
            {content.subtitle}
          </p>

          {/* Email indicator */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-sm text-gray-700 mb-4">
            <Mail className="h-4 w-4 text-[#014383]" />
            <span className="font-medium">{formData.email}</span>
          </div>

          <p className="text-sm text-gray-600 max-w-sm mx-auto">
            {content.message}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {content.checkSpam}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 px-5 py-4 tablet:px-6 border-t border-gray-100 bg-gray-50/50">
        <button
          type="button"
          onClick={() => {
            dataLayerPushClickButton(content.closeButton, 'Close');
            closeModal();
          }}
          className={cn(
            'w-full px-4 py-3 rounded-xl font-semibold text-sm',
            'bg-gray-100 text-gray-700 hover:bg-gray-200',
            'active:scale-[0.98] transition-all duration-200'
          )}
        >
          {content.closeButton}
        </button>
      </div>
    </div>
  );
}
